/**
 * CacheService for managing in-memory data caching
 */
import { createErrorLogger } from '../utils/logger';

const logger = createErrorLogger('CacheService');

class CacheService {
  constructor() {
    this.cache = {};
  }

  /**
   * Cache an item with a timestamp
   * @param {string} key - Cache key
   * @param {any} value - Value to cache
   */
  set(key, value) {
    logger.info('set', `Setting cache for key: ${key}`);
    this.cache[key] = {
      data: value,
      timestamp: Date.now()
    };
  }

  /**
   * Get an item if it exists and is not expired
   * @param {string} key - Cache key
   * @param {number} maxAge - Maximum age in milliseconds (default: 5000)
   * @returns {any|null} Cached value or null if expired/missing
   */
  get(key, maxAge = 5000) {
    const item = this.cache[key];
    if (!item) {
      logger.info('get', `Cache miss for key: ${key}`);
      return null;
    }

    // Check if cached item is expired
    const age = Date.now() - item.timestamp;
    if (age > maxAge) {
      logger.info('get', `Cache expired for key: ${key}`, { age, maxAge });
      delete this.cache[key];
      return null;
    }

    logger.info('get', `Cache hit for key: ${key}`, { age });
    return item.data;
  }

  /**
   * Clear the entire cache or a specific key
   * @param {string|null} key - Key to clear, or null to clear all
   */
  clear(key = null) {
    if (key) {
      logger.info('clear', `Clearing cache for key: ${key}`);
      delete this.cache[key];
    } else {
      logger.info('clear', 'Clearing entire cache');
      this.cache = {};
    }
  }

  /**
   * Get all keys currently in cache
   * @returns {string[]} Array of cache keys
   */
  keys() {
    return Object.keys(this.cache);
  }

  /**
   * Get cache statistics
   * @returns {object} Cache statistics
   */
  stats() {
    const keys = this.keys();
    const stats = {
      size: keys.length,
      keys: keys,
      memoryUsage: JSON.stringify(this.cache).length
    };
    logger.info('stats', 'Cache statistics retrieved', stats);
    return stats;
  }
}

// Create singleton instance
const cacheService = new CacheService();

// Export singleton instance
export default cacheService;
